import { usePage } from '@inertiajs/vue3'
import { trackEvent as sendEvent } from '@/Helpers/analytics'

// `?ref` / `?nats` is not threaded through Inertia props for clips routes, so it is read
// straight off the URL — same value the Blade landers compute server-side.
function referredBy() {
    const params = new URLSearchParams(window.location.search)
    return params.get('ref') ?? params.get('nats') ?? 'Unknown'
}

export function useClipAnalytics({ routePrefix }) {
    const page = usePage()

    // No api key (local, or a site without one) mirrors the Blade path's lti_analytics_init_js(),
    // which goes offline on empty($api_key). Helpers/analytics.js would init the SDK keyless
    // anyway, so every event would log "Must call init() before tracking" and identify() would
    // still hit the endpoint — so clips never reach it without a key.
    const trackEvent = (event, data) => {
        if (!page.props.lti_analytics?.api_key) {
            return
        }

        sendEvent(event, data)
    }

    const baseProps = (extra = {}) => ({
        location: 'Vertical Clips Feed',
        referred_by: referredBy(),
        page_location: window.location.href,
        route_prefix: routePrefix,
        ...extra,
    })

    const trackClipFeedViewed = (extra = {}) =>
        trackEvent('Vertical Clips Feed Viewed', baseProps(extra))

    const trackClipViewed = (clip, extra = {}) =>
        trackEvent('Clip Viewed', baseProps({
            clip_id: clip?.id ?? null,
            clip_slug: clip?.slug ?? null,
            clip_title: clip?.title ?? null,
            thumbnail_image_url: clip?.start_card_image ?? null,
            ...extra,
        }))

    const trackJoinCtaClicked = (ctaSource, clip, destination, extra = {}) =>
        trackEvent('Clip Join CTA Clicked', baseProps({
            cta_source: ctaSource,
            clip_id: clip?.id ?? null,
            clip_slug: clip?.slug ?? null,
            destination,
            ...extra,
        }))

    const trackClipShared = (method, clip) =>
        trackEvent('Clip Shared', baseProps({
            method,
            clip_id: clip?.id ?? null,
            clip_slug: clip?.slug ?? null,
        }))

    // One event for both directions: `liked` is the state the toggle landed on, so an
    // unlike is not a separate funnel step.
    const trackClipLiked = (liked, clip) =>
        trackEvent('Clip Liked', baseProps({
            liked,
            clip_id: clip?.id ?? null,
            clip_slug: clip?.slug ?? null,
        }))

    const trackClipCommented = (clip) =>
        trackEvent('Clip Commented', baseProps({
            clip_id: clip?.id ?? null,
            clip_slug: clip?.slug ?? null,
        }))

    // `reason` is 'source_error' (the mp4 request failed) or 'stall' (no metadata within 15s);
    // `retried` says whether the viewer had already pressed Retry once.
    const trackClipPlaybackFailed = (reason, retried, clip) =>
        trackEvent('Clip Playback Failed', baseProps({
            reason,
            retried,
            clip_id: clip?.id ?? null,
            clip_slug: clip?.slug ?? null,
        }))

    return {
        trackClipFeedViewed,
        trackClipViewed,
        trackJoinCtaClicked,
        trackClipShared,
        trackClipLiked,
        trackClipCommented,
        trackClipPlaybackFailed,
    }
}
